import { useMemo } from 'react';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';

import { FormHeaderWithStatus } from '@/components/layout/form-header-with-status';

import { Card } from '@/components/ui/card';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { FormActionButtons } from '@/components/layout/form-action-buttons';
import { useEntityForm } from '@/hooks/useEntityForm';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { SentryDropdown } from '@/components/ui/sentry-dropdown';

import type { GateFormSubmitData } from './gate-input';

interface TerminalTypeOption {
	value: string;
	label: string;
}

const createGateFormSchema = (t: (key: string) => string) =>
	z.object({
		name: z
			.string()
			.min(1, t('gates:validation.name.required'))
			.max(255, t('gates:validation.name.maxLength'))
			.transform(val => val.trim()),
		ipAddress: z
			.string()
			.min(1, t('gates:validation.ipAddress.required'))
			.max(45, t('gates:validation.ipAddress.maxLength'))
			.transform(val => val.trim()),
		gateKey: z
			.string()
			.max(255, t('gates:validation.gateKey.maxLength'))
			.optional()
			.nullable()
			.or(z.literal('')),
		terminalTypeId: z
			.string({
				required_error: t('gates:validation.terminalType.required')
			})
			.min(1, t('gates:validation.terminalType.required'))
			.regex(/^\d+$/, t('gates:validation.terminalType.invalid'))
			.refine(val => {
				const num = Number(val);
				return Number.isInteger(num) && num > 0;
			}, t('gates:validation.terminalType.positive')),
		isActive: z.boolean()
	});

export type GateFormValues = z.infer<
	ReturnType<typeof createGateFormSchema>
>;

interface GateFormProps {
	mode: 'create' | 'edit' | 'clone';
	initialData?: GateFormSubmitData;
	onSubmit: (data: GateFormSubmitData) => void;
	isSubmitting: boolean;
	terminalTypeOptions: TerminalTypeOption[];
	isTerminalTypeLoading?: boolean;
	headerTitle: string;
	onBack?: () => void;
}

export const GateForm = ({
	mode,
	initialData,
	onSubmit,
	isSubmitting,
	terminalTypeOptions,
	isTerminalTypeLoading = false,
	headerTitle,
	onBack
}: GateFormProps) => {
	const { t } = useTranslation();
	const gateFormSchema = useMemo(() => createGateFormSchema(t), [t]);
	// Clone mode uses create label since it creates a new entity
	const submitLabel = mode === 'edit' ? t('gates:actions.update') : t('gates:actions.create');

	const form = useEntityForm<GateFormValues>({
		mode,
		schema: gateFormSchema,
		defaultValues: {
			name: '',
			ipAddress: '',
			gateKey: undefined,
			terminalTypeId: '',
			isActive: true
		},
		initialData: initialData as GateFormValues | undefined
	});

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	const { control, handleSubmit } = form;

	const handleFormSubmit = (values: GateFormValues) => {
		onSubmit({
			...values,
			terminalId: initialData?.terminalId
		});
	};

	return (
		<>
			<FormHeaderWithStatus
				title={headerTitle}
				onBack={onBack}
				control={control}
				rightContent={
					<FormActionButtons
						isSubmitting={isSubmitting}
						submitLabel={submitLabel}
						validationErrors={form.formState.errors}
						formId="gate-form"
					/>
				}
			/>

			<Form {...form}>
			<form
				id="gate-form"
				onSubmit={handleSubmit(handleFormSubmit)}
				className="space-y-6"
			>
				<Card className="space-y-6">
					<div className="space-y-1">
						<h2 className="text-lg font-semibold">
							{t('gates:fields.gateDetails')}
						</h2>
					</div>
					<div className="grid gap-6 md:grid-cols-2">
						<FormField
							control={control}
							name="name"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('gates:fields.name')} *
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											value={field.value}
											placeholder={t(
												'gates:placeholders.name'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={control}
							name="terminalTypeId"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('gates:fields.terminalType')} *
									</FormLabel>
									<FormControl>
										<SentryDropdown
											options={terminalTypeOptions}
											value={String(field.value ?? '')}
											onChange={value => {
												if (Array.isArray(value)) {
													return;
												}
												field.onChange(
													value === '' ? '' : value
												);
											}}
											placeholder={
												isTerminalTypeLoading
													? t(
															'gates:placeholders.terminalTypeLoading'
														)
													: t(
															'gates:placeholders.terminalType'
														)
											}
											disabled={
												isTerminalTypeLoading ||
												terminalTypeOptions.length === 0
											}
											className="w-full"
											autoSelectFirstOption={true}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>

					<div className="grid gap-6 md:grid-cols-2">
						<FormField
							control={control}
							name="ipAddress"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('gates:fields.ipAddress')} *
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											value={field.value}
											placeholder={t(
												'gates:placeholders.ipAddress'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={control}
							name="gateKey"
							render={({ field }) => (
								<FormItem>
									<FormLabel>
										{t('gates:fields.gateKey')}
									</FormLabel>
									<FormControl>
										<Input
											{...field}
											value={field.value ?? ''}
											placeholder={t(
												'gates:placeholders.gateKey'
											)}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				</Card>
			</form>
		</Form>
		</>
	);
};
